import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, CheckCircle2, Clock,
  FileText, AlertTriangle, ExternalLink, Share2, Pencil, Trash2,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { SocialPostThumb } from '@/pages/marketing/SocialPostThumb';
import { SocialMediaCarousel, previewItemsFromZernio } from '@/pages/marketing/SocialMediaPreview';
import { ZernioPostComposer } from '@/pages/marketing/ZernioPostComposer';
import { useToast } from '@/hooks/useToast';
import {
  getZernioCredentials,
  listZernioPosts,
  listZernioAccounts,
  platformLabel,
  canEditPost,
  canDeletePost,
  deleteZernioPost,
  friendlyPlatformError,
  type ZernioPost,
} from '@/services/zernio.service';
import { toLocaleStringInTz, wallClockPartsInTz } from '@/lib/facilityTimezone';

const FETCH_LIMIT = 200;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Beyond this many chips a day cell collapses to "+N more". */
const MAX_CHIPS_PER_DAY = 3;

type StatusKey = 'draft' | 'scheduled' | 'published' | 'failed';

/** Badge + icon per status — never colour alone, so it survives greyscale. */
const STATUS_META: Record<StatusKey, {
  label: string;
  icon: typeof Clock;
  variant: 'neutral' | 'warning' | 'success' | 'error';
  dot: string;
}> = {
  draft: { label: 'Draft', icon: FileText, variant: 'neutral', dot: 'bg-slate-400' },
  scheduled: { label: 'Scheduled', icon: Clock, variant: 'warning', dot: 'bg-amber-500' },
  published: { label: 'Published', icon: CheckCircle2, variant: 'success', dot: 'bg-emerald-500' },
  failed: { label: 'Failed', icon: AlertTriangle, variant: 'error', dot: 'bg-red-500' },
};

function statusKey(status: string): StatusKey {
  const s = status.toLowerCase();
  if (s === 'published') return 'published';
  if (s === 'failed') return 'failed';
  if (s === 'draft') return 'draft';
  // queued/pending/cancelled all read as "not out yet" on a planning calendar.
  return 'scheduled';
}

function postInstant(post: ZernioPost): Date | null {
  const published = post.platforms.map((p) => p.publishedAt).find(Boolean);
  const raw = published ?? post.scheduledFor ?? post.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function inPostZone(date: Date, timezone: string | null | undefined, opts: Intl.DateTimeFormatOptions) {
  try {
    return toLocaleStringInTz(date, undefined, { ...opts, timeZone: timezone || undefined });
  } catch {
    // An unknown IANA name from the API must not blank the calendar.
    return date.toLocaleString(undefined, opts);
  }
}

function dayKeyInZone(date: Date, timezone: string | null | undefined): string {
  // No zone, or one the runtime can't resolve, reads the browser's own day.
  const p = timezone ? wallClockPartsInTz(date, timezone) : null;
  if (!p) return localDayKey(date);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

function localDayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getMonthGrid(year: number, month: number): Array<Date | null> {
  const firstDay = new Date(year, month, 1);
  const lastDate = new Date(year, month + 1, 0).getDate();
  const cells: Array<Date | null> = [];
  for (let i = 0; i < firstDay.getDay(); i++) cells.push(null);
  for (let d = 1; d <= lastDate; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

interface CalendarProps {
  /** Rendered inside the Social Media tab shell, which supplies header + padding. */
  embedded?: boolean;
}

export default function SocialCalendarPage({ embedded }: CalendarProps = {}) {
  const [monthDate, setMonthDate] = useState(() => new Date());
  const [platform, setPlatform] = useState('all');
  const [account, setAccount] = useState('all');
  const [status, setStatus] = useState('all');
  const [openPost, setOpenPost] = useState<ZernioPost | null>(null);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);

  const credentials = useQuery({
    queryKey: ['admin', 'zernio', 'credentials'],
    queryFn: getZernioCredentials,
  });
  const configured = credentials.data?.configured ?? false;

  // Status is filtered server-side when possible so the single page we fetch is
  // spent on posts the user actually wants to see.
  const posts = useQuery({
    queryKey: ['zernio', 'posts', 'calendar', status],
    queryFn: () => listZernioPosts(FETCH_LIMIT, status as never),
    enabled: configured,
  });

  const accounts = useQuery({
    queryKey: ['zernio', 'accounts'],
    queryFn: listZernioAccounts,
    enabled: configured,
  });

  const qc = useQueryClient();
  const toast = useToast();
  const [editingPost, setEditingPost] = useState<ZernioPost | null>(null);

  /**
   * Cancelling is a HARD delete in Zernio, not a status change — the post stops
   * existing and drops out of the next fetch, which is what clears it from the
   * calendar. We keep no local row, so there is nothing to reconcile: refetch
   * and the calendar is correct by construction.
   */
  const cancelMut = useMutation({
    mutationFn: (postId: string) => deleteZernioPost(postId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['zernio', 'posts'] });
      setOpenPost(null);
      toast.success('Scheduled post cancelled');
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      // The API returns 409 with a plain sentence when Zernio has already moved
      // the post on. Refetch either way, so the calendar shows the truth.
      toast.error(msg ?? 'Could not cancel that post.');
      qc.invalidateQueries({ queryKey: ['zernio', 'posts'] });
    },
  });

  const all = useMemo(() => posts.data?.posts ?? [], [posts.data]);
  const truncated = all.length >= FETCH_LIMIT;

  const platformOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const p of all) for (const pl of p.platforms) if (pl.platform) seen.add(pl.platform);
    return [{ value: 'all', label: 'All platforms' },
      ...[...seen].sort().map((p) => ({ value: p, label: platformLabel(p) }))];
  }, [all]);

  const accountOptions = useMemo(() => [
    { value: 'all', label: 'All accounts' },
    ...(accounts.data?.accounts ?? [])
      .filter((a) => a.id)
      .map((a) => ({ value: a.id, label: a.name || a.username || a.id })),
  ], [accounts.data]);

  const filtered = useMemo(() => all.filter((post) => {
    if (platform !== 'all' && !post.platforms.some((p) => p.platform === platform)) return false;
    // Accounts aren't on the normalized post, so this narrows by platform of the
    // chosen account — see the note in the page footer.
    if (account !== 'all') {
      const chosen = (accounts.data?.accounts ?? []).find((a) => a.id === account);
      if (chosen?.platform && !post.platforms.some((p) => p.platform === chosen.platform)) return false;
    }
    return true;
  }), [all, platform, account, accounts.data]);

  /** Posts bucketed by their day, in each post's own timezone. */
  const byDay = useMemo(() => {
    const map = new Map<string, ZernioPost[]>();
    for (const post of filtered) {
      const instant = postInstant(post);
      if (!instant) continue;
      const key = dayKeyInZone(instant, post.timezone);
      const list = map.get(key);
      if (list) list.push(post); else map.set(key, [post]);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (postInstant(a)?.getTime() ?? 0) - (postInstant(b)?.getTime() ?? 0));
    }
    return map;
  }, [filtered]);

  const grid = useMemo(
    () => getMonthGrid(monthDate.getFullYear(), monthDate.getMonth()),
    [monthDate],
  );

  const monthLabel = monthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const todayKey = localDayKey(new Date());
  const shiftMonth = (delta: number) =>
    setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));

  if (!credentials.isLoading && !configured) {
    return (
      <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
        {!embedded && <PageHeader title="Social Calendar" subtitle="Scheduled and published posts by date." />}
        <EmptyState
          icon={<Share2 className="h-8 w-8" />}
          title="Zernio is not connected"
          description="Ask a Prospera administrator to add your Zernio API key in Module Settings to start publishing."
        />
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-4' : 'p-6 space-y-6'}>
      {!embedded && <PageHeader title="Social Calendar" subtitle="Scheduled and published posts by date." />}

      {/* ── Toolbar: navigation + filters ─────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" onClick={() => setMonthDate(new Date())}>Today</Button>
        <div className="flex items-center gap-1">
          <button
            type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month"
            className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[10rem] text-center text-sm font-semibold text-slate-800">{monthLabel}</span>
          <button
            type="button" onClick={() => shiftMonth(1)} aria-label="Next month"
            className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="w-44">
            <Select options={platformOptions} value={platform} onChange={(v) => setPlatform((v as string) ?? 'all')} />
          </div>
          <div className="w-52">
            <Select options={accountOptions} value={account} onChange={(v) => setAccount((v as string) ?? 'all')} />
          </div>
          <div className="w-40">
            <Select
              options={[
                { value: 'all', label: 'All statuses' },
                { value: 'scheduled', label: 'Scheduled' },
                { value: 'published', label: 'Published' },
                { value: 'draft', label: 'Draft' },
                { value: 'failed', label: 'Failed' },
              ]}
              value={status}
              onChange={(v) => setStatus((v as string) ?? 'all')}
            />
          </div>
        </div>
      </div>

      {truncated && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Showing the most recent {FETCH_LIMIT} posts. Zernio cannot filter by date, so
          older months may be incomplete — narrow by status to see further back.
        </div>
      )}

      {posts.isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner /></div>
      ) : (
        <Card>
          <CardBody className="p-0">
            <div className="hidden border-b border-slate-200 md:grid md:grid-cols-7">
              {WEEKDAYS.map((d) => (
                <div key={d} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-7">
              {grid.map((day, idx) => {
                const key = day ? localDayKey(day) : null;
                const dayPosts = key ? byDay.get(key) ?? [] : [];
                const isToday = key === todayKey;
                const showAll = expandedDay === key;
                const visible = showAll ? dayPosts : dayPosts.slice(0, MAX_CHIPS_PER_DAY);
                const hidden = dayPosts.length - visible.length;

                return (
                  <div
                    key={day ? key! : `pad-${idx}`}
                    className={cn(
                      'border-b border-slate-200 p-1.5 align-top md:min-h-[7rem] md:border-r',
                      idx % 7 === 0 && 'md:border-l',
                      !day && 'bg-slate-50/60',
                      // Below md the grid collapses to a day list, where padding
                      // cells and empty days would just be blank rows.
                      (!day || dayPosts.length === 0) && 'hidden md:block',
                    )}
                  >
                    {day && (
                      <>
                        <div className="mb-1 flex items-center justify-between">
                          <span className="flex items-center gap-1.5">
                            <span className={cn(
                              'inline-grid h-5 min-w-[1.25rem] place-items-center rounded-full px-1 text-xs',
                              isToday ? 'bg-blue-600 font-semibold text-white' : 'text-slate-500',
                            )}>
                              {day.getDate()}
                            </span>
                            <span className="text-[11px] text-slate-400 md:hidden">
                              {day.toLocaleDateString(undefined, { weekday: 'short', month: 'short' })}
                            </span>
                          </span>
                        </div>

                        <div className="space-y-1">
                          {visible.map((post) => {
                            const meta = STATUS_META[statusKey(post.status)];
                            const instant = postInstant(post);
                            return (
                              <button
                                key={post.postId}
                                type="button"
                                onClick={() => setOpenPost(post)}
                                title={post.content ?? undefined}
                                className="flex w-full items-center gap-1.5 rounded border border-slate-200 bg-white p-1 text-left hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                              >
                                <SocialPostThumb post={post} className="h-7 w-7 shrink-0 rounded" />
                                <span className="min-w-0 flex-1">
                                  <span className="flex items-center gap-1">
                                    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', meta.dot)} aria-hidden="true" />
                                    <span className="truncate text-[11px] font-medium text-slate-600">
                                      {instant ? inPostZone(instant, post.timezone, { hour: 'numeric', minute: '2-digit' }) : '—'}
                                    </span>
                                  </span>
                                  <span className="block truncate text-[11px] text-slate-400">
                                    {post.platforms.map((p) => platformLabel(p.platform ?? '')).join(', ') || meta.label}
                                  </span>
                                </span>
                              </button>
                            );
                          })}

                          {hidden > 0 && (
                            <button
                              type="button"
                              onClick={() => setExpandedDay(key)}
                              className="w-full rounded px-1 py-0.5 text-left text-[11px] font-medium text-blue-600 hover:bg-blue-50"
                            >
                              +{hidden} more
                            </button>
                          )}
                          {showAll && dayPosts.length > MAX_CHIPS_PER_DAY && (
                            <button
                              type="button"
                              onClick={() => setExpandedDay(null)}
                              className="w-full rounded px-1 py-0.5 text-left text-[11px] font-medium text-slate-500 hover:bg-slate-100"
                            >
                              Show less
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {/* ── Detail view ───────────────────────────────────────────────────── */}
      <Modal
        isOpen={openPost !== null}
        onClose={() => setOpenPost(null)}
        title="Post details"
        size="lg"
      >
        {openPost && (() => {
          const meta = STATUS_META[statusKey(openPost.status)];
          const Icon = meta.icon;
          const instant = postInstant(openPost);
          const publishedAt = openPost.platforms.map((p) => p.publishedAt).find(Boolean);
          return (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant={meta.variant} size="sm">
                  <Icon className="mr-1 inline h-3 w-3" aria-hidden="true" />{meta.label}
                </Badge>
                {instant && (
                  <span className="text-sm text-slate-500">
                    {inPostZone(instant, openPost.timezone, {
                      weekday: 'short', day: 'numeric', month: 'short',
                      hour: 'numeric', minute: '2-digit',
                    })}
                    {openPost.timezone ? ` · ${openPost.timezone}` : ''}
                  </span>
                )}
              </div>

              {/* Every item, playable — a carousel is stepped through, not just its first. */}
              {(openPost.mediaItems ?? []).some((m) => m.url) && (
                <SocialMediaCarousel items={previewItemsFromZernio(openPost.mediaItems)} />
              )}

              {openPost.title && <p className="font-medium text-slate-800">{openPost.title}</p>}
              <p className="whitespace-pre-wrap text-sm text-slate-700">{openPost.content || '—'}</p>

              <div>
                <div className="mb-1.5 text-xs uppercase tracking-wide text-slate-400">Accounts</div>
                <ul className="space-y-1.5">
                  {openPost.platforms.map((p, i) => (
                    <li key={`${p.platform}-${i}`} className="flex items-center gap-2 text-sm">
                      <Badge variant="info" size="sm">{platformLabel(p.platform ?? '')}</Badge>
                      <span className="text-slate-500">{p.status}</span>
                      {p.error && (
                        // Sanitised: Zernio relays each network's own wording,
                        // which is written for an API integrator.
                        <span className="text-red-600">· {friendlyPlatformError(p.platform, p.error)}</span>
                      )}
                      {p.platformPostUrl && (
                        <a
                          href={p.platformPostUrl} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          View published post <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {publishedAt && (
                <p className="text-xs text-slate-400">
                  Published {inPostZone(new Date(publishedAt), openPost.timezone, {
                    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
                  })}
                </p>
              )}

              {/* Zernio decides what may still change: a scheduled or failed post
                  can be edited, and anything not yet published can be cancelled.
                  The API re-checks the status against Zernio before acting, so a
                  post that starts publishing between render and click is refused
                  with a clear message rather than half-changed. */}
              {(canEditPost(openPost.status) || canDeletePost(openPost.status)) && (
                <div className="flex items-center gap-1 border-t border-slate-200 pt-3">
                  {canEditPost(openPost.status) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      title="Edit scheduled post"
                      aria-label="Edit scheduled post"
                      onClick={() => {
                        setEditingPost(openPost);
                        setOpenPost(null);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canDeletePost(openPost.status) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      title="Cancel scheduled post"
                      aria-label="Cancel scheduled post"
                      disabled={cancelMut.isPending}
                      onClick={() => {
                        // Cancelling cannot be undone — Zernio deletes the post
                        // outright rather than marking it cancelled.
                        if (!window.confirm(
                          'Cancel this scheduled post? It will be deleted in Zernio and will not be published. This cannot be undone.',
                        )) return;
                        cancelMut.mutate(openPost.postId);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* Editing reuses the composer, seeded from the post: same media picker,
          same per-platform limits, same validation as creating one. */}
      <ZernioPostComposer
        isOpen={editingPost !== null}
        onClose={() => setEditingPost(null)}
        editingPost={editingPost}
        onPublished={() => {
          qc.invalidateQueries({ queryKey: ['zernio', 'posts'] });
          setEditingPost(null);
        }}
      />
    </div>
  );
}
